import { MIN_PASSWORD_LENGTH } from "./password";

/** Just the lookup surface of the i18n instance useI18N() returns. */
interface Translator {
  get: (key: string) => string;
}

/**
 * Password-policy copy that carries the configured minimum.
 *
 * Composed from single-word fragments rather than interpolated because
 * `i18n.get()` is a bare lookup with no interpolation — the same reason
 * `lockoutMessage()` in formatRetryAfter.ts is built this way.
 *
 * The older `atleast6Characters` / `atLeast6Chars` / `passwordAtLeast6Chars` /
 * `passwordShouldContain` keys still hold their fixed-number wording because
 * `apps/native` reads them directly and is read-only. Web and business use the
 * helpers below instead, so their copy tracks the env var.
 */

/** "At least 20 characters" */
export function passwordMinLengthLabel(i18n: Translator): string {
  return `${i18n.get("atLeastLabel")} ${MIN_PASSWORD_LENGTH} ${i18n.get("charactersLabel")}`;
}

/** "Password should contain at least 20 characters, one symbol or number" */
export function passwordRequirementsText(i18n: Translator): string {
  return `${i18n.get("passwordShouldContainAtLeast")} ${MIN_PASSWORD_LENGTH} ${i18n.get("charactersOneSymbolOrNumber")}`;
}

/** The policy-gate explanation shown when a legacy password is blocked at login. */
export function passwordPolicyUpdateBody(i18n: Translator): string {
  return `${i18n.get("passwordPolicyBodyPrefix")} ${MIN_PASSWORD_LENGTH} ${i18n.get("passwordPolicyBodySuffix")}`;
}

/**
 * "Please create a new password with at least 20 characters and one symbol
 * or number." — the second of two paragraphs on native's in-flow policy-gate
 * screen (the first, passwordPolicyUpdateIntro, needs no interpolation so
 * callers read it directly via i18n.get()).
 */
export function passwordPolicyUpdateInstruction(i18n: Translator): string {
  return `${i18n.get("passwordPolicyUpdateInstructionPrefix")} ${MIN_PASSWORD_LENGTH} ${i18n.get("passwordPolicyUpdateInstructionSuffix")}`;
}
